"use client"

import { useEffect, useRef, useState } from "react"
import { Volume2, VolumeX, ExternalLink } from "@/lib/icons"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { useAdStore, trackImpression, trackClick } from "@/lib/ad-store"
import { useHasConsent } from "@/components/cookie-consent"
import { isAdCooldownExpired, isAdTestModeOn, markAdShown } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { extractExoclickZoneId, exoclickIframeSrc } from "@/lib/ad-embed"
import { useTranslations } from "next-intl"

export interface InFeedVideoAdData {
  id: string
  campaign_id: string
  // Video source — one of these
  video_url?: string       // Direct mp4/webm URL
  embed_js?: string        // Network JS embed (ExoClick outstream etc.)
  // Fallback thumbnail shown before play
  thumbnail_url?: string
  // Click destination
  click_url?: string
  // Display
  title?: string
  badge_text?: string      // "Sponsored", "Ad", custom
  network?: string         // "exoclick", "trafficjunky" etc.
}

interface InFeedVideoAdProps {
  ad: InFeedVideoAdData
  className?: string
}

export function InFeedVideoAd({ ad, className }: InFeedVideoAdProps) {
  const t = useTranslations("inFeedVideoAd")
  const hasConsent = useHasConsent()
  
  // Don't load ad if no consent
  if (!hasConsent) {
    return null
  }
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const { sessionId, isPremiumUser } = useAdStore()

  const [muted, setMuted] = useState(true)
  const [playing, setPlaying] = useState(false)
  const [hasTracked, setHasTracked] = useState(false)
  const [canShow] = useState(() =>
    isAdCooldownExpired("inline_grid", useAdFrequencyStore.getState().getCooldownMinutes("inline_sponsored_ads"))
  )

  useEffect(() => {
    if (canShow) markAdShown("inline_grid")
  }, [canShow])

  // Autoplay/pause on scroll using IntersectionObserver
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting) {
          // In view — play
          videoRef.current?.play().catch(() => {})
          setPlaying(true)

          // Track impression once
          if (!hasTracked) {
            trackImpression({
              campaign_id: ad.campaign_id,
              slot_id: "grid_video",
              session_id: sessionId,
              page_url: typeof window !== "undefined" ? window.location.href : "",
              device_type: typeof window !== "undefined" && window.innerWidth < 768 ? "mobile" : "desktop",
              viewable: true,
              view_duration_ms: 0,
            })
            setHasTracked(true)
          }
        } else {
          // Out of view — pause
          videoRef.current?.pause()
          setPlaying(false)
        }
      },
      { threshold: 0.5 },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [ad.campaign_id, sessionId, hasTracked])

  // Network JS embed rendering: see the sandboxed-iframe note on the
  // ad.embed_js return branch below for why this is no longer injected
  // directly into our own document.

  // Force Ads test mode escape hatch — see the matching comment on
  // NetworkDisplayAd.tsx. Admin/super_admin accounts always read
  // isPremiumUser: true, so without this, Force Ads could never actually
  // force this to show for its only realistic tester.
  if ((isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()) return null
  if (!canShow) return null

  function handleClick() {
    // Impressions were already reported here; clicks were not, so this
    // placement showed traffic but a permanent 0% CTR.
    trackClick(ad.id, ad.campaign_id)
    if (ad.click_url) {
      window.open(ad.click_url, "_blank", "noopener,noreferrer")
    }
  }

  function toggleMute(e: React.MouseEvent) {
    e.stopPropagation()
    const newMuted = !muted
    setMuted(newMuted)
    if (videoRef.current) videoRef.current.muted = newMuted
  }

  // If network JS embed — render inside a sandboxed iframe (allow-scripts
  // only, no allow-same-origin/allow-popups/allow-top-navigation). This
  // used to inject the network's script directly into our own document,
  // which is exactly what let ExoClick's ad-provider.js hijack clicks and
  // spiral into a broken infinite retry loop elsewhere on the page (see
  // NetworkDisplayAd.tsx for the full incident writeup) — same network,
  // same script, same risk here. A sandboxed iframe cannot reach outside
  // its own frame no matter how badly the ad script behaves.
  if (ad.embed_js) {
    // See lib/ad-embed.ts — srcDoc+allow-scripts-only gives the frame an
    // opaque origin, which blocks ExoClick's cookie write and the creative
    // never renders (an empty box forever). A real cross-origin iframe at
    // ExoClick's own iframe.php endpoint gets its own real origin instead.
    const zoneId = extractExoclickZoneId(ad.embed_js)
    const srcDoc = `<!DOCTYPE html><html><head><style>html,body{margin:0;padding:0;overflow:hidden;background:transparent}</style></head><body>${ad.embed_js}</body></html>`
    // The outer box is sized by `className` from the caller — for the grid
    // placement that's `aspect-[9/16]` (booru-grid.tsx), a tall portrait box
    // (~390px on a typical ~220px-wide column). This iframe was hardcoded to
    // a fixed 250px height regardless — so on the actual grid it was both
    // handed the wrong aspect ratio (ExoClick's Outstream Video script
    // computes its own video layout against whatever box it's given, and a
    // squat 100%-wide/250-tall box bears no relation to a 9:16 slot) and left
    // up to ~140px of dead black space below it from the container's own
    // bg-black. Filling the real box it was given, instead of a guessed
    // fixed height, is the fix — same "give the ad exactly the box it should
    // fill" principle as every other placement here.
    const style = { width: "100%", height: "100%", border: 0, display: "block" as const }
    return (
      <div
        ref={containerRef}
        className={cn("relative overflow-hidden rounded-lg border border-border/50 bg-black", className)}
      >
        <Badge variant="secondary" className="absolute top-2 left-2 z-10 text-[10px] bg-muted/90 backdrop-blur-sm">
          {ad.badge_text ?? "Sponsored"}
        </Badge>
        {zoneId ? (
          <iframe src={exoclickIframeSrc(zoneId)} sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" title={t("advertisement")} scrolling="no" style={style} />
        ) : (
          <iframe srcDoc={srcDoc} sandbox="allow-scripts" title={t("advertisement")} scrolling="no" style={style} />
        )}
      </div>
    )
  }

  // Direct video URL
  return (
    <div
      ref={containerRef}
      className={cn(
        "relative overflow-hidden rounded-lg border border-border/50 bg-black cursor-pointer group",
        "transition-all duration-200 hover:border-primary/40",
        className,
      )}
      onClick={handleClick}
    >
      {/* Video */}
      <video
        ref={videoRef}
        src={ad.video_url}
        poster={ad.thumbnail_url}
        muted={muted}
        loop
        playsInline
        className="w-full h-full object-cover"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />

      {/* Sponsored badge */}
      <Badge variant="secondary" className="absolute top-2 left-2 text-[10px] bg-muted/90 backdrop-blur-sm">
        {ad.badge_text ?? "Sponsored"}
      </Badge>

      {/* Mute toggle */}
      <button
        onClick={toggleMute}
        className="absolute top-2 right-2 bg-black/60 text-white p-1.5 rounded-full hover:bg-black/80 transition-colors opacity-0 group-hover:opacity-100"
        aria-label={muted ? t("unmute") : t("mute")}
      >
        {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
      </button>

      {/* Bottom info */}
      {(ad.title || ad.click_url) && (
        <div className="absolute bottom-0 left-0 right-0 p-2.5 space-y-0.5">
          {ad.title && (
            <p className="text-xs font-medium text-white line-clamp-1">{ad.title}</p>
          )}
          {ad.click_url && (
            <div className="flex items-center gap-1 text-[10px] text-white/60 opacity-0 group-hover:opacity-100 transition-opacity">
              <ExternalLink className="h-2.5 w-2.5" />
              <span>{t("learnMore")}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
